import type { DemandGrain } from "~/utils/demandScope"
import { round } from "~/utils/math"

/**
 * Whether a workspace has moved since its publish. `workspaceVersion` is bumped by every authored
 * write (`emitDemandEditEvent`). Shared by the publish control and portfolio chip so they agree.
 */
export const isEditedSincePublish = (src: {
  workspaceVersion: number
  publishedVersion: number
}): boolean => src.workspaceVersion > src.publishedVersion

// NPI warehouse-share fan-out makes leaves fractional; untouched variants re-sum within rounding.
const UNIT_TOLERANCE = 0.5

export const snapDelta = (delta: number): number => (Math.abs(delta) <= UNIT_TOLERANCE ? 0 : delta)

/**
 * One variant's change since publish in whole units: the draft rounded once less the published
 * rounded once, so Published only moves on a publish and Now reads as Total demand does. A set of
 * variants sums theirs, so a total moves by what its rows show. The grid's vs Published cells, the
 * dialog and the explain all read it.
 */
export const wholeChange = (now: number, published: number): number =>
  snapDelta(now - published) === 0 ? 0 : Math.round(now) - Math.round(published)

// At the two decimals getDemandTotals sends, so a change near half a unit rounds the same way in
// the grid, the dialog and the explain.
export const comparableDraft = (row: { totalDemand: number; publishTail: number }): number =>
  round(row.totalDemand, 2) - round(row.publishTail, 2)

/**
 * One variant's draft-minus-published delta in whole units (`wholeChange`). The shared `G` makes
 * a scoped draft beside a whole-variant published total a compile error. `published` undefined =
 * nothing published in the remaining horizon.
 */
export const driftForVariant = <G extends DemandGrain>(
  draft: { totalDemand: number; publishTail: number; grain: G },
  // NoInfer, or a mismatched pair widens `G` to the union and compiles; the draft fixes the grain.
  published: { units: number; grain: NoInfer<G> } | undefined,
): number => wholeChange(comparableDraft(draft), published?.units ?? 0)

/**
 * Whether the plan's numbers moved from the published ones. Unlike `isEditedSincePublish`, catches
 * drift with no edit behind it (sales, pre-orders, model refreshes). `publishTail` is excluded:
 * weeks past the publish's coverage aren't a change (`getPublishDiff`'s `tailWeeks`). Both sides
 * must be whole-variant totals, or a scope filter reads as a plan change.
 */
export const hasPublishDrift = (src: {
  grain: "whole-variant"
  draft: { variantId: string; totalDemand: number; publishTail: number }[]
  publishedByVariant: Map<string, number>
}): boolean => {
  const seen = new Set<string>()

  for (const row of src.draft) {
    seen.add(row.variantId)
    const published = src.publishedByVariant.get(row.variantId) ?? 0
    if (snapDelta(comparableDraft(row) - published) !== 0) return true
  }
  // A published variant that left the collection has no draft row, but its units are still live.
  for (const [variantId, published] of src.publishedByVariant) {
    if (!seen.has(variantId) && snapDelta(-published) !== 0) return true
  }

  return false
}
